import {
  OTP_FLOW_PHASES,
  type OtpChallengeController,
  type OtpChallengeState,
} from '@wheely/web-auth-lib/react';
import React, { useState, useEffect } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';

import { getAuthErrorText } from '@/shared/utils/getAuthErrorText';
import { CodeForm } from '@/shared/view/CodeForm';

import s from './style.module.css';

/**
 * Phone-conflict handoff: the phone already has an account, so registration
 * hands over a running phone sign-in that links the verified email. The
 * controller comes ready-made, hence the manual subscription.
 */
const PhoneHandoff: React.VFC<{
  controller: OtpChallengeController;
  /** The number the code went to, in the same E.164 form the request carried. */
  phone: string;
}> = ({ controller, phone }) => {
  const intl = useIntl();
  const [state, setState] = useState<OtpChallengeState>(() => controller.getState());

  useEffect(() => {
    setState(controller.getState());

    return controller.subscribe(setState);
  }, [controller]);

  const errorText = state.error ? getAuthErrorText(intl, state.error) : undefined;

  if (state.phase === OTP_FLOW_PHASES.DONE) {
    return null;
  }

  return (
    <div data-test-id="phone-handoff">
      <p className={s.note}>
        <FormattedMessage
          defaultMessage="This phone number already has an account. Enter the code we sent to {phone} to sign in — your verified email will be attached."
          id="login_phone_handoff_note"
          description="Phone conflict handoff note"
          values={{ phone: <b>{phone}</b> }}
        />
      </p>

      {state.phase === OTP_FLOW_PHASES.AWAITING_CODE ||
      state.phase === OTP_FLOW_PHASES.VERIFYING ? (
        <CodeForm
          description={undefined}
          disabled={state.phase === OTP_FLOW_PHASES.VERIFYING}
          error={errorText}
          attemptsLeft={state.attemptsLeft}
          resendAvailableIn={state.resendAvailableIn}
          onSubmit={code => controller.submit(code)}
          onResend={() => controller.resend()}
        />
      ) : (
        <p>{errorText}</p>
      )}
    </div>
  );
};

export { PhoneHandoff };
